import { getRecentSearches, RecentSearchesResponse } from "common/apis";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/useAuth";

/**
 * The signed-in user's recent searches.
 *
 * Keyed on the user, and reading its token from the reactive auth state rather
 * than from Storage, for a specific reason: the app sets `staleTime: Infinity`,
 * so a result cached under a key is never refetched on its own. With a static
 * `["recentSearches"]` key, the empty list fetched while logged out stayed
 * cached forever — logging in changed neither the key nor the staleness, so
 * recents stayed empty until the app was relaunched and the cache was fresh.
 *
 * Putting `user?.id` in the key means signing in is a *different* query with
 * nothing cached, which fetches. It also keeps one user's recents from being
 * served to the next one who signs in. Mirrors `useSavedSearches`.
 */
export const useRecentSearches = () => {
  const { user } = useAuth();

  return useInfiniteQuery<RecentSearchesResponse, Error>({
    queryKey: ["recentSearches", user?.id],
    queryFn: async ({ pageParam }) => {
      const token = user?.accessToken || "";
      const page = pageParam as number;
      return getRecentSearches(token, page, 5, "recent");
    },
    getNextPageParam: (lastPage) => {
      // Add safety check for pagination
      if (!lastPage?.pagination) {
        return undefined;
      }

      const { current_page, last_page } = lastPage.pagination;
      if (current_page < last_page) {
        return current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    // Recents are per-user and need a token; logged out there is nothing to
    // ask for. Callers still see `isLoading: false` here, so this does not
    // strand anyone in a loading state.
    enabled: !!user,
    // Add these options for better error handling
    retry: 1,
    retryDelay: 1000,
  });
};
